import React, { useState, useEffect } from 'react';
import { useAffiliateTracking } from './hooks/useAffiliateTracking';
import { auth, db } from './firebase';
import { signOut, onAuthStateChanged, applyActionCode } from "firebase/auth";
import { doc, getDoc, updateDoc, arrayUnion, increment } from "firebase/firestore";
import { Loader2, ArrowRight, CheckCircle } from 'lucide-react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';

const Dashboard = lazy(() => import('./Dashboard'));
const OnboardingFlow = lazy(() => import('./components/OnboardingFlow'));
const Registration = lazy(() => import('./Registration'));
const Login = lazy(() => import('./Login'));
const ResetPassword = lazy(() => import('./ResetPassword'));
const ScrollToTop = lazy(() => import('./components/ScrollToTop'));



function App() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [verificationStatus, setVerificationStatus] = useState(null); // 'processing' | 'success' | 'error'

    // ─── Tracking du code de parrainage (?ref=CODE dans l'URL) ───
    useAffiliateTracking();


    // ─── PROTECTION ANTI-COPIE ET ANTI-INSPECTION ────────────
    useEffect(() => {
        // Bloquer le clic droit (menu contextuel)
        const preventContextMenu = (e) => {
            e.preventDefault();
        };

        // Bloquer les raccourcis claviers spécifiques (F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U)
        const preventKeyBoardShortcuts = (e) => {
            // F12
            if (e.keyCode === 123) {
                e.preventDefault();
            }
            // Ctrl+Shift+I (Inspecter) ou Ctrl+Shift+J (Console) ou Ctrl+Shift+C
            if (e.ctrlKey && e.shiftKey && (e.keyCode === 73 || e.keyCode === 74 || e.keyCode === 67)) {
                e.preventDefault();
            }
            // Ctrl+U (Voir le code source)
            if (e.ctrlKey && e.keyCode === 85) {
                e.preventDefault();
            }
        };

        // Ajouter les écouteurs d'événements
        document.addEventListener('contextmenu', preventContextMenu);
        document.addEventListener('keydown', preventKeyBoardShortcuts);

        // Nettoyage lors du démontage
        return () => {
            document.removeEventListener('contextmenu', preventContextMenu);
            document.removeEventListener('keydown', preventKeyBoardShortcuts);
        };
    }, []);

    // ─── INTERCEPTEUR GLOBAL DES ACTIONS FIREBASE ────────────
    // Au lieu de dépendre du routeur (qui attend que `user` soit chargé et
    // redirige souvent hâtivement vers /login), on intercepte directement l'URL
    // dès le premier rendu, peu importe la route.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const mode = params.get('mode');
        const oobCode = params.get('oobCode');

        if (!mode || !oobCode) return;

        console.log('[Firebase Action] URL interceptée:', { mode, oobCode });

        if (mode === 'resetPassword') {
            console.log('[Firebase Action] Redirection forcée vers /reset-password');
            // On utilise replace() pour ne pas encombrer l'historique et on passe le oobCode
            // de façon sécurisée (encodé). L'application va se recharger proprement sur la bonne route.
            window.location.replace(`/reset-password?oobCode=${encodeURIComponent(oobCode)}`);
            return;
        }

        if (mode === 'verifyEmail') {
            console.log('[Firebase Action] Mode verifyEmail → application du code...');
            setVerificationStatus('processing');
            // Nettoyer l'URL pour éviter de re-traiter le code à un rechargement
            window.history.replaceState({}, document.title, window.location.pathname);

            applyActionCode(auth, oobCode)
                .then(async () => {
                    console.log('[Firebase Action]  Email vérifié avec succès!');
                    if (auth.currentUser) {
                        await auth.currentUser.reload();
                        setUser(prev => prev ? { ...prev, emailVerified: true } : prev);
                    }
                    setVerificationStatus('success');
                })
                .catch((err) => {
                    console.error('[Firebase Action]  Erreur:', err.code, err.message);
                    // Si le code est invalide, le lien a peut-être déjà été utilisé
                    // Dans ce cas, on recharge quand même l'utilisateur pour vérifier
                    if (auth.currentUser) {
                        auth.currentUser.reload().then(() => {
                            if (auth.currentUser?.emailVerified) {
                                setVerificationStatus('success');
                                setUser(prev => prev ? { ...prev, emailVerified: true } : prev);
                            } else {
                                setVerificationStatus('error');
                            }
                        }).catch(() => setVerificationStatus('error'));
                    } else {
                        // Utilisateur non connecté (navigateur in-app mobile)
                        // L'email est quand même vérifié côté Firebase.
                        // L'utilisateur doit se connecter pour voir le dashboard.
                        setVerificationStatus('needs-login');
                    }
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- 1. SESSION ---
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                try {
                    await currentUser.reload();
                } catch (e) {
                    console.warn("Reload impossible (no network?)", e.message);
                }

                const docRef = doc(db, "users", currentUser.uid);
                let firestoreData = {};
                try {
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        firestoreData = docSnap.data();
                        
                        // Si le compte est bloqué, on déconnecte de force
                        if (firestoreData.isBlocked) {
                            console.log("[Security] Compte bloqué par l'administrateur.");
                            await signOut(auth);
                            setUser(null);
                            setLoading(false);
                            return;
                        }
                    }
                } catch (e) { console.log("Profil incomplet", e); }

                const fullName = firestoreData.firstName || currentUser.displayName || "Candidat";

                const userData = {
                    ...currentUser,
                    ...firestoreData,
                    prenom: fullName,
                    emailVerified: auth.currentUser?.emailVerified ?? currentUser.emailVerified,
                };
                setUser(userData);

                // --- 2. TRACKING JOURNALIER DES CONNEXIONS ---
                try {
                    if (!firestoreData.isAdmin) {
                        const today = new Date().toISOString().split('T')[0];
                        if (firestoreData.lastLoginDate !== today) {
                            updateDoc(docRef, {
                                lastLoginDate: today,
                                loginCount: increment(1),
                                loginHistory: arrayUnion(today)
                            }).then(() => {
                                console.log(`[Tracking] Nouvelle journée de connexion : ${today}`);
                            }).catch((trackingErr) => {
                                console.error("[Tracking] Erreur enregistrement connexion:", trackingErr);
                            });
                        }
                    }
                } catch (e) {
                    console.error("[Tracking] Erreur non gérée:", e);
                }

                // Pas besoin de showWelcome ici, le router s'en charge via userData.hasSeenWelcome
            } else {
                setUser(null);
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // --- HEARTBEAT (Update lastActive) ---
    useEffect(() => {
        if (!user || !user.uid) return;

        const updateActivity = async () => {
            try {
                // On met à jour l'activité seulement si l'onglet est actif
                if (document.visibilityState === 'visible') {
                    await updateDoc(doc(db, 'users', user.uid), {
                        lastActive: new Date().toISOString()
                    });
                }
            } catch (e) { /* silencieux */ }
        };

        // Mise à jour immédiate
        updateActivity();

        // Puis toutes les 10 minutes (réduction de charge Firebase)
        const interval = setInterval(updateActivity, 10 * 60 * 1000);
        return () => clearInterval(interval);
    }, [user?.uid]);


    const handleLogout = async () => { await signOut(auth); };

    if (loading) return <div className="flex h-screen bg-background items-center justify-center"><Loader2 size={64} className="animate-spin text-primary" /></div>;

    // Écran de traitement du code Firebase (mode=verifyEmail dans l'URL)
    if (verificationStatus === 'processing') {
        return (
            <div className="flex flex-col h-screen bg-background items-center justify-center gap-4">
                <Loader2 size={48} className="animate-spin text-primary" />
                <p className="text-text font-semibold">Vérification de votre e-mail en cours...</p>
            </div>
        );
    }

    // Scénario mobile : lien ouvert dans un navigateur in-app (Gmail, Outlook) sans session
    // L'email est vérifié côté Firebase, mais l'utilisateur doit se connecter ici.
    if (verificationStatus === 'needs-login') {
        return (
            <div className="flex flex-col h-screen bg-background items-center justify-center gap-6 p-6 text-center">
                <div className="w-20 h-20 bg-emerald-500/15 rounded-full flex items-center justify-center">
                    <CheckCircle size={40} className="text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold text-text">E-mail vérifié avec succès !</h2>
                <p className="text-text/70 max-w-sm">
                    Votre adresse e-mail a été confirmée. Connectez-vous maintenant pour accéder à votre espace AXE CANADA.
                </p>
                <a
                    href="/login"
                    className="bg-primary text-white font-bold px-8 py-3.5 rounded-xl hover:opacity-90 transition flex items-center gap-2"
                >
                    Se connecter <ArrowRight size={18} />
                </a>
            </div>
        );
    }

    return (
        <Suspense fallback={
            <div className="flex h-screen bg-background items-center justify-center flex-col gap-4">
                <Loader2 size={40} className="animate-spin text-primary" />
                <p className="text-text/70 animate-pulse text-sm">Chargement d'AXE CANADA...</p>
            </div>
        }>
            <ScrollToTop />
            <Routes>
                {/* AUTHENTICATION ROUTES */}
                <Route path="/register" element={
                    !user ? <Registration /> : <Navigate to="/dashboard/cockpit" />
                } />
                <Route path="/login" element={
                    !user ? <Login /> : <Navigate to="/dashboard/cockpit" />
                } />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* FIREBASE ACTION ROUTE */}
                <Route path="/__/auth/action" element={<Navigate to={user ? "/dashboard/cockpit" : "/login"} />} />

                {/* PROTECTED ROUTES */}
                <Route path="/" element={<Navigate to={user ? "/dashboard/cockpit" : "/login"} />} />
                
                <Route path="/onboarding" element={
                    user ? (user.hasSeenWelcome ? <Navigate to="/dashboard/jobs" replace /> : <div className="bg-background min-h-screen text-text"><OnboardingFlow user={user} onComplete={() => setUser(prev => ({ ...prev, hasSeenWelcome: true }))} /></div>) : <Navigate to="/login" />
                } />

                <Route path="/dashboard/*" element={
                    user ? (
                        user.hasSeenWelcome ? (
                            <Dashboard user={user} onLogout={handleLogout} />
                        ) : <Navigate to="/onboarding" replace />
                    ) : <Navigate to="/login" />
                } />
                {/* Fallback route */}
                <Route path="*" element={<Navigate to="/" />} />
            </Routes>
        </Suspense>
    );
}

export default App;
